import { NgModule } from "@angular/core";
import { Route, RouterModule, Routes } from "@angular/router";
import { CallbackComponent } from "./auth/callback/callback.component";
import { HomeComponent } from "./components/home/home.component";
import { ExpedientesCrearComponent } from "@components/expedientes-crear/expedientes-crear.component";
import { ExpedientesListarComponent } from "@components/expedientes-listar/expedientes-listar.component";
import { UnauthorizedComponent } from "@components/unauthorized/unauthorized.component";
import Funcionalidades from "@shared/enums/funcionalidades.enum";
import { authGuard, authGuardAction } from "./guards/auth.guard";
import { ExpedientesDetalleComponent } from "@components/expedientes-detalle/expedientes-detalle.component";
import { ExpedientesDetalleProrrogaComponent } from "@components/expedientes-detalle-prorroga/expedientes-detalle-prorroga.component";
import AccionesFuncionalidad from "@shared/enums/acciones.enum";
import { ExpedientesDetalleSubirDocumentosComponent } from "@components/expedientes-detalle-subir-documentos/expedientes-detalle-subir-documentos.component";
import { ExpedientesDetalleHistorialComponent } from "@components/expedientes-detalle-historial/expedientes-detalle-historial.component";
import { ExpedientesDetalleVerDocumentosComponent } from "@components/expedientes-detalle-ver-documentos/expedientes-detalle-ver-documentos.component";
import { VerDocumentoComponent } from "@components/expedientes-detalle-ver-documentos/ver-documento/ver-documento.component";
import { ExpedientesDetalleApelacionComponent } from "@components/expedientes-detalle-apelacion/expedientes-detalle-apelacion.component";
import { PlantillaDocumentoComponent } from "@components/plantillas-documentos/plantillas-documentos.component";
import { NotFoundComponentComponent } from "@components/not-found-component/not-found-component.component";
import { ExpedientesVerDocumentosComponent } from "@components/expedientes-ver-documentos/expedientes-ver-documentos.component";
import { IngresoComponent } from "@components/ingreso/ingreso.component";
import { ConflictoSesionPageComponent } from "./auth/conflicto-sesion-page/conflicto-sesion-page.component";
import { conflictoSesionGuard } from "./guards/conflicto-sesion.guard";

function agregarRutasHijasExpediente(rutasHijas: Routes): Routes {
	return [
		{
			path: "",
			component: ExpedientesListarComponent,
			data: { breadcrumb: false },
		},
		{
			path: ":id",
			canActivate: [authGuardAction],
			data: { accion: AccionesFuncionalidad.ver_expediente },
			children: [
				{
					path: "",
					component: ExpedientesDetalleComponent,
					data: { breadcrumb: false },
				},
				...rutasHijas
			],
		},
	];
}

function agregarRutasHijasLicencia(rutasHijas: Routes): Routes {
	return [
		{
			path: "licencia",
			canActivate: [authGuardAction],
			data: { accion: AccionesFuncionalidad.ver_antecedentes },
			children: [
				{
					path: "",
					component: ExpedientesDetalleVerDocumentosComponent,
					data: { breadcrumb: false },
				},
				...rutasHijas
			],
		},
	];
}

function licenciasVerDocumento(reemplazar: boolean = false): Routes {
	return [
		{
			path: "ver-documento",
			canActivate: [authGuardAction],
			data: { accion: AccionesFuncionalidad.ver_documento },
			children: [
				{
					path: "",
					component: VerDocumentoComponent,
					data: { breadcrumb: false },
				},
				...(reemplazar ? [licenciasReemplazarDocumento] : [])
			]
		}
	]
};

const licenciasSubirDocumento: Route = {
	path: "subir-documento",
	canActivate: [authGuardAction],
	component: ExpedientesDetalleSubirDocumentosComponent,
	data: { accion: AccionesFuncionalidad.ingresar_antecedentes },
};

const licenciasReemplazarDocumento: Route = {
	path: "reemplazar",
	canActivate: [authGuardAction],
	component: ExpedientesDetalleSubirDocumentosComponent,
	data: { accion: AccionesFuncionalidad.reemplazar_documento },
};

const expedienteEstados: Route = {
	path: "estados",
	canActivate: [authGuardAction],
	component: ExpedientesDetalleHistorialComponent,
	data: {
		accion: AccionesFuncionalidad.ver_historial,
	},
};

const expedienteVerDocumentos: Route = {
	path: "ver-documentos",
	canActivate: [authGuardAction],
	component: ExpedientesVerDocumentosComponent,
	data: {
		accion: AccionesFuncionalidad.ver_documentos_expediente,
	},
};

const routes: Routes = [
	{ path: "", redirectTo: "/inicio", pathMatch: "full" },
	{ path: "inicio", component: HomeComponent, canActivate: [conflictoSesionGuard] },
	{ path: "login/callback", component: CallbackComponent },
	{ path: "conflicto-sesion", component: ConflictoSesionPageComponent },

	{
		path: "expedientes",
		canActivate: [conflictoSesionGuard],
		children: [
			// crear expediente
			{
				path: "crear",
				component: ExpedientesCrearComponent,
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.crear_expediente },
			},
			// listar expedientes creados
			{
				path: "creados",
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.revisar_expediente },
				children: agregarRutasHijasExpediente([]),
			},
			// listar prórrogas solicitadas
			{
				path: "prorrogas",
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.revisar_prorrogas },
				children: agregarRutasHijasExpediente([]),
			},
			// listar expedientes para revisar antecedentes fiscalizador
			{
				path: "antecedentes",
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.revisar_antecedentes },
				children: agregarRutasHijasExpediente(agregarRutasHijasLicencia(licenciasVerDocumento())),
			},
			// listar historial expedientes
			{
				path: "historial",
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.revisar_expedientes_historial },
				children: agregarRutasHijasExpediente([expedienteEstados, expedienteVerDocumentos]),
			},
			// listar resoluciones a firmar por el presidente
			{
				path: "resoluciones",
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.revisar_resoluciones_a_firmar },
				children: agregarRutasHijasExpediente(agregarRutasHijasLicencia(licenciasVerDocumento())),
			},
			// listar apelaciones enviadas por el prestador
			{
				path: "apelaciones",
				canActivate: [authGuard],
				data: { funcionalidad: Funcionalidades.revisar_apelaciones },
				children: agregarRutasHijasExpediente([
						{
							path: "apelacion",
							canActivate: [authGuardAction],
							component: ExpedientesDetalleApelacionComponent,
							data: { accion: AccionesFuncionalidad.ver_apelacion }
						},
					])
			},
		],
	},
	// listar expedientes a los que se les solicito antecedentes
	{
		path: "solicitudes-antecedentes",
		canActivate: [conflictoSesionGuard, authGuard],
		data: { funcionalidad: Funcionalidades.revisar_solicitudes },
		children: agregarRutasHijasExpediente([
			...agregarRutasHijasLicencia([...licenciasVerDocumento(true), licenciasSubirDocumento]),
			{
				path: "prorroga",
				canActivate: [authGuardAction],
				component: ExpedientesDetalleProrrogaComponent,
				data: {
					accion: AccionesFuncionalidad.solicitar_prorroga,
				},
			},
			/* {
				path: "licencia/subir-documento",
				canActivate: [authGuardAction],
				component: ExpedientesDetalleSubirDocumentosComponent,
				data: {
					accion: AccionesFuncionalidad.ingresar_antecedentes,
				},
			},
			{
				path: "licencia/ver-documento/reemplazar",
				canActivate: [authGuardAction],
				component: ExpedientesDetalleSubirDocumentosComponent,
				data: {
					accion: AccionesFuncionalidad.ingresar_antecedentes,
				},
			},
			{
				path: "apelacion",
				canActivate: [authGuardAction],
				component: ExpedientesDetalleApelacionComponent,
				data: {
					accion: AccionesFuncionalidad.solicitar_apelacion,
				},
			}, */
		]),
	},
	{
		path: "plantillas-documentos",
		canActivate: [conflictoSesionGuard, authGuard],
		component: PlantillaDocumentoComponent,
		data: { funcionalidad: Funcionalidades.ver_plantillas },
	},
	{ path: "ingresar", component: IngresoComponent },

	// Ruta sin autorización
	{ path: "unauthorized", component: UnauthorizedComponent },

	// catch all route
	{ path: "**", component: NotFoundComponentComponent },
];

@NgModule({
	imports: [RouterModule.forRoot(routes)],
	exports: [RouterModule],
})
export class AppRoutingModule {}
